import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCOP, formatDate, formatApiError } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { HelpCircle } from "lucide-react";
import { useOnboardingTour } from "../hooks/useOnboardingTour";

const PREV_BADGE = {
  apto: { label: "Apto", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  posiblemente_apto: { label: "Requiere revisión", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  no_apto: { label: "No apto", cls: "bg-rose-50 text-rose-800 border-rose-300" },
  out_of_scope: { label: "Fuera de alcance", cls: "bg-indigo-50 text-indigo-800 border-indigo-300" },
  datos_insuficientes: { label: "Datos incompletos", cls: "bg-gray-100 text-gray-700 border-gray-300" },
};

const STATE_LABEL = {
  draft_intake: "Borrador",
  nivel0_pending: "Prevalidación en curso",
  nivel0_ready: "Prevalidación lista",
  out_of_scope: "Fuera de alcance",
  nivel1_payment_pending: "Pendiente pago Diagnóstico",
  nivel1_paid: "Pago Diagnóstico confirmado",
  nivel1_processing: "Procesando Diagnóstico",
  nivel1_needs_human_review: "En revisión humana",
  nivel1_revision_requested: "Revisión solicitada",
  nivel1_delivered: "Diagnóstico entregado",
  nivel2_offer_sent: "Oferta Kit Documental enviada",
  nivel2_payment_pending: "Pendiente pago Kit Documental",
  nivel2_paid: "Pago Kit Documental confirmado",
  nivel2_processing: "Procesando Kit Documental",
  nivel2_needs_human_review: "Revisión Kit Documental humana",
  nivel2_revision_requested: "Revisión Kit Documental solicitada",
  nivel2_delivered: "Kit Documental entregado",
  closed: "Cerrado",
};

export default function UserDashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [prevByCase, setPrevByCase] = useState({}); // case_id -> {result}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Iter25: tour onboarding (auto en primera visita; botón "Tour" lo re-abre)
  const { startTour, resetTour } = useOnboardingTour();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/cases");
        if (!alive) return;
        setCases(data);

        // Para cada caso, intenta cargar prevalidation (404 es OK).
        const entries = await Promise.all(
          data.map(async (c) => {
            try {
              const { data: p } = await api.get(`/cases/${c.id}/prevalidation`);
              return [c.id, p];
            } catch (err) {
              // 404 esperado si caso aún no tiene prevalidación
              if (process.env.NODE_ENV === "development" && err?.response?.status !== 404) {
                console.warn(`Prevalidation load failed for case ${c.id}:`, err);
              }
              return [c.id, null];
            }
          })
        );
        if (alive) {
          const map = {};
          entries.forEach(([id, p]) => { if (p) map[id] = p; });
          setPrevByCase(map);
        }
      } catch (e) {
        if (alive) setError(formatApiError(e?.response?.data?.detail));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="user-dashboard" className="container-prose py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 pb-6 border-b border-gray-200">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Panel</p>
          <h1 className="font-serif text-3xl text-gray-900">Hola, {user?.name || "usuario"}</h1>
          <p className="text-sm text-gray-600 mt-1">{user?.email}</p>
        </div>
        <Button asChild size="lg" className="rounded-sm h-11" data-testid="dashboard-new-case-button" data-tour="dashboard-new-case">
          <Link to="/cases/new">Prevalidar nuevo caso</Link>
        </Button>
        <button
          type="button"
          onClick={() => { resetTour(); startTour(); }}
          className="absolute top-4 right-4 sm:static sm:ml-2 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-brand-burgundy"
          data-testid="topbar-help-button"
          title="Volver a ver el tour de bienvenida"
        >
          <HelpCircle className="w-4 h-4" /> Tour
        </button>
      </div>

      {loading && (
        <div className="border border-gray-200 p-8 text-sm text-gray-500" data-testid="cases-loading">
          Cargando casos…
        </div>
      )}

      {!loading && error && (
        <div className="border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm">{error}</div>
      )}

      {!loading && !error && cases.length === 0 && (
        <div data-testid="cases-empty-state" className="border border-gray-200 p-12 text-center bg-gray-50">
          <p className="font-serif text-2xl text-gray-900 mb-2">Aún no tienes casos</p>
          <p className="text-sm text-gray-600 mb-6">
            Crea tu primer caso. La prevalidación gratuita se ejecuta automáticamente.
          </p>
          <Button asChild className="rounded-sm" data-testid="cases-empty-cta">
            <Link to="/cases/new">Crear primer caso</Link>
          </Button>
        </div>
      )}

      {!loading && !error && cases.length > 0 && (
        <div className="border border-gray-200 bg-white overflow-x-auto" data-testid="cases-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="px-5 py-3 text-left font-medium">Caso</th>
                <th className="px-3 py-3 text-right font-medium whitespace-nowrap">Valor estimado</th>
                <th className="px-3 py-3 text-left font-medium">Estado</th>
                <th className="px-3 py-3 text-left font-medium">Prevalidación</th>
                <th className="px-3 py-3 text-right font-medium">Creado</th>
                <th className="px-5 py-3 text-right font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => {
                const prev = prevByCase[c.id];
                const badge = prev ? PREV_BADGE[prev.result] : null;
                const stateLabel = STATE_LABEL[c.state] || c.state;
                return (
                  <tr key={c.id} data-testid={`case-row-${c.id}`} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 min-w-[260px]">
                      <Link to={`/cases/${c.id}`} className="block">
                        <div className="font-medium text-gray-900 truncate max-w-[420px]">{c.title}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[420px] mt-0.5">{c.objeto_contractual}</div>
                      </Link>
                    </td>
                    <td className="px-3 py-4 text-right text-gray-800 font-medium whitespace-nowrap">{formatCOP(c.valor_estimado_cop)}</td>
                    <td className="px-3 py-4">
                      <span className="inline-flex border border-gray-300 bg-gray-50 px-2 py-[3px] text-[11px] uppercase tracking-wider text-gray-700">
                        {stateLabel}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      {badge ? (
                        <Link
                          to={`/cases/${c.id}/prevalidation`}
                          data-testid={`prev-badge-${c.id}`}
                          className={`inline-flex border px-2 py-[3px] text-[11px] uppercase tracking-wider ${badge.cls}`}
                        >
                          {badge.label}
                        </Link>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-4 text-right text-xs text-gray-500 whitespace-nowrap">{formatDate(c.created_at)}</td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/cases/${c.id}`}
                          className="text-xs text-primary hover:underline"
                          data-testid={`case-view-${c.id}`}
                        >
                          Ver
                        </Link>
                        {c.state === "nivel1_payment_pending" && (
                          <Link
                            to={`/cases/${c.id}/payment`}
                            className="text-xs bg-primary text-white px-2 py-1 hover:bg-primary/90 transition-colors"
                            data-testid={`case-pay-${c.id}`}
                          >
                            Pagar Diagnóstico
                          </Link>
                        )}
                        {(c.state === "nivel1_delivered" || c.state === "nivel2_offer_sent" || c.state?.startsWith("nivel2_")) && (
                          <Link
                            to={`/cases/${c.id}`}
                            className="text-xs bg-emerald-700 text-white px-2 py-1 hover:bg-emerald-800 transition-colors"
                            data-testid={`case-download-link-${c.id}`}
                          >
                            Descargar PDF
                          </Link>
                        )}
                        {prev && (
                          <Link
                            to={`/cases/${c.id}/prevalidation`}
                            className="text-xs border border-primary text-primary px-2 py-1 hover:bg-primary hover:text-white transition-colors"
                            data-testid={`case-prevalidation-${c.id}`}
                          >
                            Prevalidación
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
